import type { Locale } from "../i18n/locale";
import type { AddonInfraConfig } from "./addonInfra";
import { EC2_SKUS, RDS_SKUS, type Ec2SkuId, type RdsSkuId } from "./addonInfra";
import type { SavingsLevers } from "./savingsLevers";
import { DEFAULT_SAVINGS_LEVERS } from "./savingsLevers";
import type { LineItem } from "./types";
import type { Workload } from "./types";

const HOURS_PER_MONTH = 730;
const RDS_STORAGE_USD_PER_GB = 0.115;

/** Multi-AZ: ~second instance + sync (indicative × on instance + storage uplift). */
const RDS_MULTIAZ_INSTANCE_FACTOR = 1.85;
const RDS_MULTIAZ_STORAGE_FACTOR = 1.15;

export type FrontendMode = "apigw" | "cloudfront" | "ec2_spa";
export type BackendMode = "lambda" | "ec2" | "eks";
/** Primary persistence: Dynamo-only in model, RDS, or split. */
export type DatabaseMode = "dynamo" | "rds" | "dynamo_and_rds";

/** Public = типовий інтернет-facing API; private = без публічного доступу до застосунку, більше NAT/endpoints. */
export type NetworkExposure = "public" | "private";

export type DeploymentStackConfig = {
  frontend: FrontendMode;
  backend: BackendMode;
  database: DatabaseMode;
  /** Internet-facing vs isolated (VPN/DX/corp); впливає на мережеві рядки та план. */
  networkExposure: NetworkExposure;
  rdsSku: RdsSkuId;
  rdsStorageGb: number;
  rdsMultiAz: boolean;
  /** Pick RDS class from DAU when true. */
  rdsAutoSize: boolean;
  ec2AppSku: Ec2SkuId;
  ec2AppCount: number;
  ec2AppAutoCount: boolean;
  /** Route 53 hosted zone + DNS queries (custom domain). ACM public certs for ALB/CF/API GW usually free. */
  route53Enabled: boolean;
  /** ALB + AWS WAF indicative line (LCU, rules, request volume). */
  albWafEnabled: boolean;
  /** Amazon GuardDuty threat detection (VPC/DNS/CloudTrail heuristic). */
  guardDutyEnabled: boolean;
};

export const DEFAULT_DEPLOYMENT_STACK: DeploymentStackConfig = {
  frontend: "apigw",
  backend: "lambda",
  database: "dynamo",
  networkExposure: "public",
  rdsSku: "db.t3.small",
  rdsStorageGb: 50,
  rdsMultiAz: false,
  rdsAutoSize: true,
  ec2AppSku: "t3.medium",
  ec2AppCount: 2,
  ec2AppAutoCount: true,
  route53Enabled: false,
  albWafEnabled: false,
  guardDutyEnabled: false,
};

const EC2_IDS = new Set(EC2_SKUS.map((s) => s.id));
const RDS_IDS = new Set(RDS_SKUS.map((s) => s.id));

function coerceFrontend(x: unknown): FrontendMode {
  if (x === "cloudfront" || x === "ec2_spa") return x;
  return "apigw";
}

function coerceBackend(x: unknown): BackendMode {
  if (x === "ec2" || x === "eks") return x;
  return "lambda";
}

function coerceDatabase(x: unknown): DatabaseMode {
  if (x === "rds" || x === "dynamo_and_rds") return x;
  return "dynamo";
}

function coerceNetworkExposure(x: unknown): NetworkExposure {
  if (x === "private") return "private";
  return "public";
}

/** Heuristic app server count from DAU (effective). */
export function autoEc2AppCount(dau: number): number {
  if (dau < 2_000) return 1;
  if (dau < 10_000) return 2;
  if (dau < 50_000) return 4;
  return Math.min(16, 6 + Math.floor(dau / 25_000));
}

/** Heuristic RDS class from DAU. */
export function autoRdsSku(dau: number): RdsSkuId {
  if (dau < 3_000) return "db.t3.micro";
  if (dau < 15_000) return "db.t3.small";
  if (dau < 80_000) return "db.t3.medium";
  return "db.m7g.large";
}

export function normalizeDeploymentStack(
  raw: unknown,
  legacyAddon?: AddonInfraConfig | null,
): DeploymentStackConfig {
  const base = { ...DEFAULT_DEPLOYMENT_STACK };

  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    base.frontend = coerceFrontend(o.frontend);
    base.backend = coerceBackend(o.backend);
    base.database = coerceDatabase(o.database);
    base.networkExposure = coerceNetworkExposure(o.networkExposure);
    const rsRaw = o.rdsSku;
    const rs =
      typeof rsRaw === "string" && RDS_IDS.has(rsRaw as RdsSkuId)
        ? (rsRaw as RdsSkuId)
        : base.rdsSku;
    base.rdsSku = rs;
    base.rdsStorageGb = Math.min(
      2000,
      Math.max(20, Math.round(Number(o.rdsStorageGb) || base.rdsStorageGb)),
    );
    base.rdsMultiAz = Boolean(o.rdsMultiAz);
    base.rdsAutoSize = o.rdsAutoSize !== false;
    const esRaw = o.ec2AppSku;
    const es =
      typeof esRaw === "string" && EC2_IDS.has(esRaw as Ec2SkuId)
        ? (esRaw as Ec2SkuId)
        : base.ec2AppSku;
    base.ec2AppSku = es;
    base.ec2AppCount = Math.min(
      48,
      Math.max(1, Math.round(Number(o.ec2AppCount) || base.ec2AppCount)),
    );
    base.ec2AppAutoCount = o.ec2AppAutoCount !== false;
    base.route53Enabled = Boolean(o.route53Enabled);
    base.albWafEnabled = Boolean(o.albWafEnabled);
    base.guardDutyEnabled = Boolean(o.guardDutyEnabled);
  }

  if (legacyAddon?.rdsEnabled && base.database === "dynamo") {
    base.database = "rds";
    base.rdsSku = legacyAddon.rdsSku;
    base.rdsStorageGb = legacyAddon.rdsStorageGb;
  }
  if (legacyAddon?.ec2Enabled && base.backend === "lambda") {
    base.backend = "ec2";
    base.ec2AppSku = legacyAddon.ec2Sku;
    base.ec2AppCount = legacyAddon.ec2Count;
    base.ec2AppAutoCount = false;
  }

  return base;
}

/** For infra plan / exports: “is EC2 in stack?”. */
export function stackHasEc2App(s: DeploymentStackConfig): boolean {
  return s.backend === "ec2";
}

export function stackHasRds(s: DeploymentStackConfig): boolean {
  return s.database === "rds" || s.database === "dynamo_and_rds";
}

function findEc2(id: Ec2SkuId) {
  return EC2_SKUS.find((x) => x.id === id) ?? EC2_SKUS[2];
}

function findRds(id: RdsSkuId) {
  return RDS_SKUS.find((x) => x.id === id) ?? RDS_SKUS[1];
}

export type DeploymentCostAdjust = {
  lambdaFactor: number;
  dynamoFactor: number;
};

export function deploymentCostAdjust(
  stack: DeploymentStackConfig,
): DeploymentCostAdjust {
  let lambdaFactor = 1;
  if (stack.backend === "ec2") lambdaFactor = 0.03;
  else if (stack.backend === "eks") lambdaFactor = 0.12;

  let dynamoFactor = 1;
  if (stack.database === "rds") dynamoFactor = 0.1;
  else if (stack.database === "dynamo_and_rds") dynamoFactor = 0.45;

  return { lambdaFactor, dynamoFactor };
}

/**
 * Extra line items (us-east-1 ref $/mo before regional factor), plus notes.
 */
export function deploymentStackLineItems(
  stack: DeploymentStackConfig,
  w: Workload,
  locale: Locale,
  savings: SavingsLevers = DEFAULT_SAVINGS_LEVERS,
): { items: LineItem[]; assumptions: string[] } {
  const items: LineItem[] = [];
  const assumptions: string[] = [];
  const dau = w.usersDaily;

  const monthlyReq = w.usersDaily * w.requestsPerUser * 30;
  /** ~5/7 uptime if turning off dev-style compute on weekends (Sat–Sun). */
  const wkCompute = savings.scheduleOffWeekends ? 5 / 7 : 1;

  if (stack.frontend === "cloudfront") {
    const egressGb = Math.max(5, monthlyReq * 0.000_4);
    const cfUsd = 3 + egressGb * 0.085;
    items.push({
      id: "cloudfront",
      label: locale === "uk" ? "Amazon CloudFront (CDN)" : "Amazon CloudFront (CDN)",
      monthlyUsd: cfUsd,
      detail:
        locale === "uk"
          ? `Орієнтир: base + ~${egressGb.toFixed(0)} GB egress @ edge`
          : `Indicative: base + ~${egressGb.toFixed(0)} GB egress at edge`,
    });
    assumptions.push(
      locale === "uk"
        ? "CloudFront: спрощена модель трафіку; реальність залежить від cache hit."
        : "CloudFront: simplified traffic model; real cost depends on cache hit ratio.",
    );
  }

  if (stack.frontend === "ec2_spa") {
    const sku = findEc2("t3.small");
    const m = sku.hourlyUsd * HOURS_PER_MONTH * wkCompute;
    items.push({
      id: "ec2_spa",
      label: locale === "uk" ? "EC2 — хостинг SPA / nginx" : "EC2 — SPA / nginx host",
      monthlyUsd: m,
      detail:
        locale === "uk"
          ? `1 × ${sku.id}, on-demand ~${HOURS_PER_MONTH} год/міс`
          : `1 × ${sku.id}, on-demand ~${HOURS_PER_MONTH} h/mo`,
    });
    assumptions.push(
      locale === "uk"
        ? savings.scheduleOffWeekends
          ? "SPA на EC2: без ALB у базовому рядку; вихідні вимкнено — ×5/7 до годин."
          : "SPA на EC2: без ALB/сертифікатів у рядку (ACM безкоштовний для CF/ALB)."
        : savings.scheduleOffWeekends
          ? "SPA on EC2: no ALB in base line; weekend shutdown modeled as ×5/7 on hours."
          : "SPA on EC2: ALB/certs not itemized (ACM free with ALB/CF).",
    );
  }

  if (stack.backend === "ec2") {
    const count = stack.ec2AppAutoCount ? autoEc2AppCount(dau) : stack.ec2AppCount;
    const sku = findEc2(stack.ec2AppSku);
    const m = count * sku.hourlyUsd * HOURS_PER_MONTH * wkCompute;
    items.push({
      id: "ec2_app",
      label:
        locale === "uk"
          ? `EC2 — бекенд (ASG орієнтир)`
          : `EC2 — backend (ASG indicative)`,
      monthlyUsd: m,
      detail:
        locale === "uk"
          ? `${count} × ${sku.id}, on-demand; ${stack.ec2AppAutoCount ? "кількість від DAU" : "вручну"}`
          : `${count} × ${sku.id}, on-demand; ${stack.ec2AppAutoCount ? "count from DAU" : "manual"}`,
    });
    assumptions.push(
      locale === "uk"
        ? savings.scheduleOffWeekends
          ? "EC2 бекенд: Lambda знижена; години EC2 ×5/7 якщо вимкнено на вихідних; ALB/EBS — окремо якщо не увімкнено WAF/ALB у стеку."
          : "EC2 бекенд: Lambda у моделі знижена до мінімуму; без ALB/EBS окремо."
        : savings.scheduleOffWeekends
          ? "EC2 backend: Lambda minimized; EC2 hours ×5/7 with weekend shutdown lever; ALB/EBS separate unless ALB/WAF stack option on."
          : "EC2 backend: Lambda row minimized; ALB/EBS not itemized.",
    );
  }

  if (stack.backend === "eks") {
    const controlPlane = 73;
    const nodeHourly = 0.096;
    const nodes = 2;
    const nodeMonthly = nodes * nodeHourly * HOURS_PER_MONTH * wkCompute;
    const m = controlPlane + nodeMonthly;
    items.push({
      id: "eks",
      label: locale === "uk" ? "Amazon EKS (кластер)" : "Amazon EKS (cluster)",
      monthlyUsd: m,
      detail:
        locale === "uk"
          ? `Control plane + ${nodes}×m5.large-подібні worker (орієнтир)`
          : `Control plane + ${nodes}×m5.large-class workers (indicative)`,
    });
    const eksUk: string[] = [
      stack.networkExposure === "private"
        ? "EKS: без Fargate; NAT/ALB частково в «приватній мережі»."
        : "EKS: без Fargate; NAT/ALB не деталізовано.",
    ];
    if (savings.scheduleOffWeekends) {
      eksUk.push("Worker-години ×5/7 якщо кластер вимикається на вихідних.");
    }
    eksUk.push("~2 worker ноди в рядку.");
    const eksEn: string[] = [
      stack.networkExposure === "private"
        ? "EKS: no Fargate; NAT/ALB partly in private-network line."
        : "EKS: no Fargate; NAT/ALB not itemized.",
    ];
    if (savings.scheduleOffWeekends) {
      eksEn.push("Worker hours ×5/7 if cluster off weekends.");
    }
    eksEn.push("Rough 2 workers in line.");
    assumptions.push(locale === "uk" ? eksUk.join(" ") : eksEn.join(" "));
  }

  if (stackHasRds(stack)) {
    const skuId = stack.rdsAutoSize ? autoRdsSku(dau) : stack.rdsSku;
    const sku = findRds(skuId);
    let inst = sku.hourlyUsd * HOURS_PER_MONTH * wkCompute;
    let storage = stack.rdsStorageGb * RDS_STORAGE_USD_PER_GB;
    if (stack.rdsMultiAz) {
      inst *= RDS_MULTIAZ_INSTANCE_FACTOR;
      storage *= RDS_MULTIAZ_STORAGE_FACTOR;
    }
    const m = inst + storage;
    items.push({
      id: "rds",
      label:
        locale === "uk" ? `Amazon RDS (${sku.labelUk})` : `Amazon RDS (${sku.labelEn})`,
      monthlyUsd: m,
      detail:
        locale === "uk"
          ? `${skuId}, ${stack.rdsStorageGb} GB gp3${stack.rdsMultiAz ? ", Multi-AZ орієнтир" : ", single-AZ"}; ${stack.rdsAutoSize ? "клас від DAU" : "клас вручну"}`
          : `${skuId}, ${stack.rdsStorageGb} GB gp3${stack.rdsMultiAz ? ", Multi-AZ" : ", single-AZ"}; ${stack.rdsAutoSize ? "class from DAU" : "manual class"}`,
    });
    assumptions.push(
      locale === "uk"
        ? savings.scheduleOffWeekends
          ? "RDS: години інстансу ×5/7 при вихідних вимкнено; сховище gp3 без змін; replicas/Extended Support — не в рядку."
          : "RDS: read replicas, бекапи понад free tier, Extended Support — не в рядку."
        : savings.scheduleOffWeekends
          ? "RDS: instance hours ×5/7 with weekend shutdown; storage unchanged; replicas/Extended Support not itemized."
          : "RDS: replicas, backups beyond free tier, Extended Support — not itemized.",
    );
  }

  if (stack.route53Enabled) {
    const zoneUsd = 0.5;
    const queriesUsd = Math.max(0.35, (monthlyReq / 2e6) * 0.4);
    const m = zoneUsd + queriesUsd;
    items.push({
      id: "route53",
      label:
        locale === "uk"
          ? "Amazon Route 53 (зона + DNS)"
          : "Amazon Route 53 (zone + DNS)",
      monthlyUsd: m,
      detail:
        locale === "uk"
          ? "1 hosted zone + орієнтир публічних запитів (від трафіку API)"
          : "1 hosted zone + public query baseline (from API traffic)",
    });
    assumptions.push(
      locale === "uk"
        ? "Route 53: спрощено; health checks, складні політики — додатково. ACM публічні сертифікати для ALB/CF/API GW зазвичай $0."
        : "Route 53: simplified; health checks and advanced policies cost extra. ACM public certs for ALB/CF/API GW are usually $0.",
    );
  }

  if (stack.albWafEnabled) {
    const albBase = 16.2;
    const albLcu = Math.max(4, monthlyReq * 0.000008);
    const wafAcl = 5;
    const wafReq = (monthlyReq / 1e6) * 1.2;
    const m = albBase + albLcu + wafAcl + wafReq;
    items.push({
      id: "alb_waf",
      label:
        locale === "uk" ? "ALB + AWS WAF (орієнтир)" : "ALB + AWS WAF (indicative)",
      monthlyUsd: m,
      detail:
        locale === "uk"
          ? "LCU + web ACL + запити; managed rule groups — додатково"
          : "LCU + web ACL + requests; managed rule groups extra",
    });
    assumptions.push(
      locale === "uk"
        ? "ALB/WAF: один рядок для публічного L7; WAF на API Gateway без ALB — інша схема, сума орієнтир."
        : "ALB/WAF: single line for public L7; WAF on API Gateway without ALB differs — treat as indicative.",
    );
  }

  if (stack.guardDutyEnabled) {
    const base = 14;
    const volume = Math.min(42, 8 + monthlyReq * 0.000018 + dau * 0.00035);
    const m = base + volume;
    items.push({
      id: "guardduty",
      label:
        locale === "uk"
          ? "Amazon GuardDuty (орієнтир)"
          : "Amazon GuardDuty (indicative)",
      monthlyUsd: m,
      detail:
        locale === "uk"
          ? "VPC/DNS/CloudTrail обсяги — спрощено"
          : "VPC/DNS/CloudTrail volume — simplified",
    });
    assumptions.push(
      locale === "uk"
        ? "GuardDuty: не враховано multi-account delegated admin детально; Malware Protection / EKS — окремо."
        : "GuardDuty: multi-account admin not detailed; Malware Protection / EKS extra.",
    );
  }

  if (stack.networkExposure === "private") {
    const natHourly = 0.045;
    const natCount = 2;
    const natFixed = natCount * natHourly * HOURS_PER_MONTH;
    const egressGb = Math.max(
      20,
      monthlyReq * 0.00006 + monthlyReq * w.aiRequestShare * 0.00025,
    );
    const natDataProcessing = egressGb * 0.045;
    const vpceFlat = 22;
    const m = natFixed + natDataProcessing + vpceFlat;
    items.push({
      id: "vpc_private",
      label:
        locale === "uk"
          ? "Приватна мережа (NAT + VPC endpoints, орієнтир)"
          : "Private network (NAT + VPC endpoints, indicative)",
      monthlyUsd: m,
      detail:
        locale === "uk"
          ? `~${natCount}× NAT + ~${egressGb.toFixed(0)} GB обробки + база endpoints`
          : `~${natCount}× NAT + ~${egressGb.toFixed(0)} GB processed + baseline endpoints`,
    });
    assumptions.push(
      locale === "uk"
        ? "Приватний VPC: спрощено 2 NAT (HA); реальність — endpoints замість NAT для AWS API, один NAT у dev."
        : "Private VPC: simplified 2 NATs (HA); real deployments often add endpoints for AWS APIs and fewer NATs in dev.",
    );
  }

  return { items, assumptions };
}
